import { useCallback, useEffect, useMemo } from 'react'
import { DetectedMeeting, GoogleMeetMeeting, RecallDetectedMeeting } from '@highlight/common'
import { useGlobalStore } from '@highlight/ipc/react'
import { Toast, useToast } from '../../index'
import { Button } from '@highlight/ui'
import { useAudioNoteActiveNoteId, useIsTakingNotes } from '@renderer/hooks/audio/use-audio-note-selectors'
import { useStartAudioNote } from '@renderer/hooks/useStartAudioNote'
import { useMeetingMode } from '@renderer/hooks/settings/useMeetingMode'
import { useCalendarEvents } from '@renderer/hooks/use-calendar-events'
import { useOpenBrief } from '@renderer/overlay/Sidepanel/components/meeting/brief/use-open-brief'
import { BriefPreview } from '@renderer/overlay/Sidepanel/components/meeting/brief/components/brief-preview'
import { useMatchingCalendarEvent } from './hooks'
import { calendarEventToScheduledMeeting } from '@shared/utils/connections/calendars'
import { isRecallDetectedMeeting } from '@shared/utils/audio-notes/AudioNoteUtils'
import AudioNoteIpcRenderer from '@renderer/ipc/AudioNoteIpc.renderer'
import { AmplitudeRenderer } from '@renderer/utils/amplitude.renderer'
import { useIncognitoMode } from '@renderer/hooks/useIncognitoMode'
import { useOutOfMeetingHours } from '@renderer/overlay/Sidepanel/components/meeting/use-out-of-meeting-hours'
import { ADD_HOURS_TO_TRANSCRIBE_CTA, useOpenHoursPaywall } from './out-of-meeting-hours'
import { AutopilotIntroToast } from './autopilot-intro-toast'
import HighlightLogoIcon from '@renderer/components/Icons/HighlightLogoIcon'

const AUTOPILOT_INTRO_TOAST_ID = 'autopilot-intro'

interface MeetingStartToastProps {
  meeting: DetectedMeeting | RecallDetectedMeeting
  toastId: string
  /**
   * The user was in this call during onboarding, so autopilot held off. Ask
   * before transcribing, then explain that later meetings start on their own.
   */
  firstMeetingAfterOnboarding?: boolean
}

export function MeetingStartToast({ meeting, toastId, firstMeetingAfterOnboarding = false }: MeetingStartToastProps) {
  const isTakingNotes = useIsTakingNotes()
  const { startAudioNote } = useStartAudioNote()
  const { isIncognitoEnabled, disableIncognito } = useIncognitoMode()
  const { meetingMode } = useMeetingMode()
  const openBrief = useOpenBrief()
  const outOfHours = useOutOfMeetingHours()
  const openHoursPaywall = useOpenHoursPaywall()
  const toast = useToast()
  const briefAudioNoteId = useAudioNoteActiveNoteId()
  const briefCalendarEventId =
    'calendarEventId' in meeting && typeof meeting.calendarEventId === 'string' ? meeting.calendarEventId : null
  const { data: allCalendarEvents } = useCalendarEvents()
  const briefCalendarEvent = useMemo(
    () => (briefCalendarEventId ? allCalendarEvents.find((e) => e.id === briefCalendarEventId) : undefined),
    [briefCalendarEventId, allCalendarEvents],
  )

  const onOpenBrief = useCallback(
    (e: React.MouseEvent) => {
      e.stopPropagation()
      if (!briefCalendarEventId) return
      AmplitudeRenderer.trackEvent('Meeting Brief', {
        action: 'view',
        source: 'meeting-start-toast',
        meeting_id: briefCalendarEventId,
      })
      openBrief({
        calendarEventId: briefCalendarEventId,
        audioNoteId: briefAudioNoteId ?? undefined,
        isActive: true,
        title: meeting.title,
        meeting: briefCalendarEvent,
      })
      toast.dismiss(toastId)
    },
    [briefCalendarEventId, briefAudioNoteId, briefCalendarEvent, openBrief, meeting.title, toast, toastId],
  )

  const isRecallMeeting = isRecallDetectedMeeting(meeting)
  const recallCalendarEvent = isRecallMeeting ? meeting.calendarEvent : undefined
  const hookMatchingCalendarEvent = useMatchingCalendarEvent(meeting)
  const matchingCalendarEvent = recallCalendarEvent || hookMatchingCalendarEvent

  const [notificationsEnabled] = useGlobalStore<boolean>('settings', 'notifications.meetingDetected', true)

  // Auto-dismiss when notifications disabled. The first-meeting prompt stays:
  // it is autopilot asking for consent, not a notification, and dismissing it
  // would leave the call neither recorded nor asked about.
  useEffect(() => {
    if (!notificationsEnabled && !firstMeetingAfterOnboarding) {
      toast.dismiss(toastId)
    }
  }, [notificationsEnabled, firstMeetingAfterOnboarding, toast, toastId])

  const onStart = useCallback(
    async (e: React.MouseEvent) => {
      e.stopPropagation()

      // The gate would refuse this start, so skip it and its side effects. A
      // note in progress must not be stopped for a start that never happens.
      if (outOfHours) {
        openHoursPaywall(matchingCalendarEvent ?? undefined)
        toast.dismiss(toastId)
        return
      }

      if (isIncognitoEnabled) {
        await disableIncognito()
      }

      if (isTakingNotes) {
        await AudioNoteIpcRenderer.stopRecording(true)
      }

      let meetingToUse: DetectedMeeting | GoogleMeetMeeting | RecallDetectedMeeting
      if (isRecallMeeting) {
        meetingToUse = meeting
      } else if (matchingCalendarEvent) {
        meetingToUse = calendarEventToScheduledMeeting(matchingCalendarEvent)
      } else {
        meetingToUse = meeting
      }

      const onSuccessfulStart = firstMeetingAfterOnboarding
        ? () => {
            toast.add({
              analytics: { type: 'autopilot intro' },
              id: AUTOPILOT_INTRO_TOAST_ID,
              icon: <HighlightLogoIcon size={16} />,
              title: 'Transcription started',
              duration: 30_000,
              autoExpandDelayMs: 0,
              autoCollapseDelayMs: 10_000,
              body: (introToastId) => <AutopilotIntroToast toastId={introToastId} />,
            })
          }
        : undefined
      startAudioNote({ source: 'toast', meeting: meetingToUse, onSuccessfulStart })
      AmplitudeRenderer.trackEvent('Audio Note Action', {
        action: 'start taking notes',
        type: firstMeetingAfterOnboarding ? 'first meeting after onboarding' : 'detected meeting',
        hasCalendarMatch: !!matchingCalendarEvent,
        isRecallMeeting,
      })
      toast.dismiss(toastId)
    },
    [
      toast,
      toastId,
      isTakingNotes,
      startAudioNote,
      meeting,
      matchingCalendarEvent,
      isRecallMeeting,
      isIncognitoEnabled,
      disableIncognito,
      outOfHours,
      openHoursPaywall,
      firstMeetingAfterOnboarding,
    ],
  )

  const description = isIncognitoEnabled
    ? 'Starting this transcription will turn off incognito mode'
    : isTakingNotes
      ? 'Save your current note and start a new note?'
      : firstMeetingAfterOnboarding
        ? 'Highlight can transcribe your meetings. Start with this one?'
        : 'Would you like to take notes?'

  // Out of hours the click only opens the paywall, so the CTA must not promise
  // a join or a transcript it is about to refuse.
  const startLabel = outOfHours
    ? ADD_HOURS_TO_TRANSCRIBE_CTA
    : isIncognitoEnabled || firstMeetingAfterOnboarding
      ? 'Start Transcribing'
      : meetingMode === 'autopilot'
        ? 'Join & Transcribe'
        : 'Join Meeting'

  return (
    <Toast toastId={toastId}>
      <Toast.Content>
        {briefCalendarEvent?.brief && (
          <BriefPreview
            brief={briefCalendarEvent.brief.brief}
            relevantLinks={briefCalendarEvent.brief.relevantLinks}
            onClick={onOpenBrief}
            disableLinks
          />
        )}
        <Toast.Footer>
          <span>{description}</span>
          <Button onClick={onStart} size="sm" variant="primary">
            {startLabel}
          </Button>
        </Toast.Footer>
      </Toast.Content>
    </Toast>
  )
}
